import {
  Button,
  ColumnTable,
  DisplayInfo,
  Icon,
  Loading,
  Table,
} from '@octano/global-ui';
import { useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router';
import { Col, Row } from 'reactstrap';
import DisplayError from '../../../components/info/DisplayError';
import { SectionTitle } from '../../../components/text';
import { formatDate } from '../../../utils/dates';
import { PathsLayouts } from '../../../config/routes';
import { TranslationsKeys } from '../../../locales/translations';
import { useRiskEvaluationsDataLoader } from '../loaders/StudentRiskEvaluationSessionsDataLoader';
import { StudentRiskEvaluationSession } from '../types';
import StudentRiskEvaluationSessionsParticipantModal, {
  StudentRiskEvaluationSessionsParticipantModalMethods,
} from './StudentRiskEvaluationSessionsParticipantModal';

const StudentRiskEvaluationSessionsTable = () => {
  const { t } = useTranslation(
    TranslationsKeys.STUDENT_RISK_EVALUATION_SESSIONS_LIST,
  );
  const history = useHistory();
  const participantsModalRef = useRef<StudentRiskEvaluationSessionsParticipantModalMethods>(
    null,
  );
  const {
    loading,
    query,
    result,
    updateQuery,
  } = useRiskEvaluationsDataLoader();

  const manage = useCallback(
    (row: StudentRiskEvaluationSession) => {
      if (!query.schoolId) {
        return;
      }
      history.push(
        `${PathsLayouts.studentWarnings}/evaluation-sessions/students/${row.id}`,
      );
    },
    [query, history],
  );

  const columns = useMemo<ColumnTable<StudentRiskEvaluationSession>[]>(
    () => [
      {
        columnName: 'name',
        tdClassName: 'text-center',
        thClassName: 'text-center',
        headerText: t(`table.headers.name`),
      },
      {
        columnName: 'startAt',
        tdClassName: 'text-center',
        thClassName: 'text-center',
        headerText: t(`table.headers.startAt`),
        cellFormat: ({ value }) => formatDate(value, 'DD/MM/YYYY'),
      },
      {
        columnName: 'endAt',
        tdClassName: 'text-center',
        thClassName: 'text-center',
        headerText: t(`table.headers.endAt`),
        cellFormat: ({ value }) => formatDate(value, 'DD/MM/YYYY'),
      },
      {
        columnName: 'school',
        tdClassName: 'text-center',
        thClassName: 'text-center',
        headerText: t(`table.headers.school`),
        cellFormat: ({ row }) => <span>{row?.school?.name || ''}</span>,
      },
      {
        columnName: 'description',
        tdClassName: 'text-center',
        thClassName: 'text-center',
        headerText: t(`table.headers.description`),
      },
      {
        columnName: 'participants',
        tdClassName: 'text-center',
        thClassName: 'text-center',
        headerText: t(`table.headers.participants`),
        cellFormat: ({ row }) => {
          return (
            <div>
              <button
                className="btn btn-sm"
                type="button"
                onClick={() =>
                  participantsModalRef?.current?.open(
                    row.name,
                    row?.participants || [],
                  )
                }
              >
                <Icon name="information" />
              </button>
            </div>
          );
        },
      },
      {
        columnName: 'action',
        tdClassName: 'text-center',
        thClassName: 'text-center',
        headerText: t(`table.headers.action`),
        cellFormat: ({ row }) => (
          <div>
            <Button
              text={t(`table.actions.manage`)}
              size="sm"
              onClick={() => manage(row)}
            />
            <button
              className="btn btn-sm"
              type="button"
              onClick={() =>
                history.push(
                  `${PathsLayouts.studentWarnings}/evaluation-sessions/update/${row.id}`,
                )
              }
            >
              <Icon name="edit" />
            </button>
          </div>
        ),
      },
    ],
    [t, manage, history],
  );

  if (!query?.schoolId) {
    return (
      <div className="mt-4 d-block">
        <DisplayInfo
          insideCard
          title={t(`noFiltersSelected.title`)}
          textBody={t(`noFiltersSelected.description`)}
        />
      </div>
    );
  }

  if (!result?.success && loading) {
    return (
      <>
        <Loading textBody={''} />
      </>
    );
  }

  if (!result?.success) {
    return (
      <DisplayError
        insideCard
        title={t(`queryError.title`)}
        textBody={t(`queryError.description`)}
      />
    );
  }

  return (
    <>
      <div className="w-100 mt-4 d-block">
        <Row className="mb-3 d-flex justify-content-between">
          <Col xs={12} md={6} className="d-flex align-items-center">
            <SectionTitle text={t(`tableTitle`)} />
          </Col>
          <Col xs={12} md={6}>
            <div className="d-flex justify-content-end">
              <Button
                text={t(`createSession`)}
                className="ml-2"
                size="md"
                type="button"
                icon="plus"
                onClick={() => {
                  history.push(
                    `${PathsLayouts.studentWarnings}/evaluation-sessions/create`,
                  );
                }}
              />
            </div>
          </Col>
        </Row>
        <Table
          data={result?.data?.data ?? []}
          columns={columns}
          isLoadingResults={loading}
          pagination={{
            itemsPerPage: 10,
            currentPage: query.page,
            totalItems: result.data.total,
            totalPages: Math.ceil(result.data.total / 10),
            onChangePage: (nextPage) => {
              updateQuery({
                ...query,
                page: nextPage,
              });
            },
          }}
        />
      </div>
      <StudentRiskEvaluationSessionsParticipantModal
        ref={participantsModalRef}
      />
    </>
  );
};

export default StudentRiskEvaluationSessionsTable;
